import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  fetchConfigPromotionStats,
  type ConfigPromotionStats,
} from "../api/client";

/** Promotion-rate leaderboard. Surfaces saved TPE configs that have
 *  consumed compute but produced no live promotions, so we can stop
 *  re-running them. Three signal categories drive the sort order:
 *
 *    🛑 never used        config exists in TpeFeatureConfigsTable but has
 *                         0 SUCCEEDED TPE runs.
 *    🛑 never promoted    config has been run on ≥3 distinct days, each
 *                         ≥3 days apart, and produced 0 (run × ticker)
 *                         promotions. Hard retirement candidate.
 *    ⚠️ low rate          ran enough to qualify, has *some* promotions
 *                         but a low promotion rate (denominator counted
 *                         per run × ticker pair, retired models count).
 *    ✓  producing         healthy promotion rate.
 *
 *  Color usage: per the project CVD memory, the chips lean on icon +
 *  text first; orange/blue/grey luminance second. No red↔green pairing,
 *  no purple↔blue distinctions. */

type StatusKey =
  | "never_used"
  | "never_promoted"
  | "flaky_gate"  // promote keeps failing the CV consistency gate — config search space broken
  | "low_rate"
  | "producing";

interface StatusVisual {
  key: StatusKey;
  label: string;
  glyph: string;
  bg: string;
  border: string;
  fg: string;
  tooltip: string;
}

function classify(c: ConfigPromotionStats): StatusVisual {
  if (c.never_used) {
    return {
      key: "never_used",
      label: "Never run",
      glyph: "🛑",
      bg: "#1c1917",
      border: "#92400e",
      fg: "#fed7aa",
      tooltip: "This config has never been kicked off in a TPE run. Pure cold storage.",
    };
  }
  if (c.never_promoted) {
    return {
      key: "never_promoted",
      label: "Never promoted",
      glyph: "🛑",
      bg: "#1c1917",
      border: "#c2410c",
      fg: "#fed7aa",
      tooltip: `Ran on ${c.well_spaced_run_count}+ distinct days ≥3 days apart and produced 0 promotions. Strong retirement candidate.`,
    };
  }
  // "Flaky gate" = promote-time CV gate keeps rejecting this config's
  // Phase 2 winners. ≥3 attempted (run × ticker) pairs and >50% of them
  // ended with nothing promoted. Signals the search space is producing
  // unstable winners — config likely needs tuning or retirement.
  if (c.rejection_only_total >= 3 && c.rejection_only_rate > 0.5) {
    return {
      key: "flaky_gate",
      label: "Flaky CV gate",
      glyph: "⚠️",
      bg: "#1c1917",
      border: "#92400e",
      fg: "#fed7aa",
      tooltip:
        `${c.rejection_only_total}/${c.run_ticker_pairs_total} (run × ticker) attempts ended with no promotion because every candidate failed the consistency gate. ` +
        `Total audit rows: ${c.rejection_event_count} (walk-down means multiple per pair). ` +
        `Last rejection: ${c.last_rejection_at ? fmtDate(c.last_rejection_at) : "—"}.`,
    };
  }
  // "Low rate" = some promotions, but <10% of (run × ticker) pairs panned out.
  if (c.run_ticker_pairs_total >= 10 && c.promotion_rate < 0.1) {
    return {
      key: "low_rate",
      label: "Low rate",
      glyph: "⚠️",
      bg: "#1c1917",
      border: "#c2410c",
      fg: "#fed7aa",
      tooltip: `${c.promotions_total}/${c.run_ticker_pairs_total} (run × ticker) pairs ever produced a promotion. Watch list.`,
    };
  }
  return {
    key: "producing",
    label: "Producing",
    glyph: "✓",
    bg: "#0c1e3d",
    border: "#1e40af",
    fg: "#bfdbfe",
    tooltip: `Healthy promotion rate (${(c.promotion_rate * 100).toFixed(1)}% of run × ticker pairs).`,
  };
}

function fmtUsd(v: number): string {
  if (!Number.isFinite(v) || v <= 0) return "—";
  if (v < 1) return `$${v.toFixed(2)}`;
  if (v < 100) return `$${v.toFixed(1)}`;
  return `$${Math.round(v).toLocaleString()}`;
}

function fmtPct(rate: number, denom: number): string {
  if (denom <= 0) return "—";
  return `${(rate * 100).toFixed(1)}%`;
}

function fmtDate(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toISOString().slice(0, 10);
}

/** Tiny inline sparkline of run dates so the user can eyeball how clumped
 *  / spaced the runs were. Renders as one dot per run on a single line
 *  spanning the date range. Helps validate the well_spaced_run_count at
 *  a glance for "never promoted" rows. */
function DateSparkline({ dates }: { dates: string[] }) {
  if (dates.length === 0) {
    return <span style={{ color: "#475569", fontSize: 11 }}>—</span>;
  }
  if (dates.length === 1) {
    return <span style={{ color: "#94a3b8", fontSize: 11 }}>{dates[0]}</span>;
  }
  const ts = dates.map((d) => new Date(d).getTime()).filter((t) => !Number.isNaN(t));
  if (ts.length < 2) {
    return <span style={{ color: "#94a3b8", fontSize: 11 }}>{dates.length} runs</span>;
  }
  const lo = Math.min(...ts);
  const hi = Math.max(...ts);
  const span = hi - lo;
  const width = 140;
  const height = 10;
  return (
    <div title={dates.join("  ·  ")}>
      <svg width={width} height={height} style={{ display: "block" }}>
        <line x1={0} y1={height / 2} x2={width} y2={height / 2} stroke="#1e293b" strokeWidth={1} />
        {ts.map((t, i) => {
          const x = span > 0 ? ((t - lo) / span) * (width - 4) + 2 : width / 2;
          return <circle key={i} cx={x} cy={height / 2} r={2.5} fill="#56B4E9" />;
        })}
      </svg>
      <div style={{ fontSize: 9, color: "#64748b", marginTop: 1 }}>
        {dates[0]} → {dates[dates.length - 1]}
      </div>
    </div>
  );
}

type SortKey = "default" | "cost" | "rate" | "runs" | "name";

export function ConfigLeaderboardPage() {
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["config-promotion-stats"],
    queryFn: fetchConfigPromotionStats,
    staleTime: 60_000,
  });

  const [sortKey, setSortKey] = useState<SortKey>("default");
  const [hideHealthy, setHideHealthy] = useState(false);

  const rows = useMemo(() => {
    if (!data) return [] as ConfigPromotionStats[];
    const filtered = hideHealthy
      ? data.configs.filter((c) =>
          c.never_used ||
          c.never_promoted ||
          (c.rejection_only_total >= 3 && c.rejection_only_rate > 0.5) ||
          (c.run_ticker_pairs_total >= 10 && c.promotion_rate < 0.1))
      : data.configs;
    const sorted = [...filtered];
    switch (sortKey) {
      case "cost":
        sorted.sort((a, b) => b.total_cost_usd - a.total_cost_usd);
        break;
      case "rate":
        sorted.sort((a, b) => a.promotion_rate - b.promotion_rate);
        break;
      case "runs":
        sorted.sort((a, b) => b.runs_total - a.runs_total);
        break;
      case "name":
        sorted.sort((a, b) => a.config_name.localeCompare(b.config_name));
        break;
      // default — backend already sorts retirement candidates first.
    }
    return sorted;
  }, [data, sortKey, hideHealthy]);

  const summary = useMemo(() => {
    if (!data) return { totalConfigs: 0, neverUsed: 0, neverPromoted: 0, flakyGate: 0, wastedCost: 0 };
    let neverUsed = 0;
    let neverPromoted = 0;
    let flakyGate = 0;
    let wastedCost = 0;
    for (const c of data.configs) {
      if (c.never_used) neverUsed++;
      if (c.never_promoted) {
        neverPromoted++;
        wastedCost += c.total_cost_usd;
      }
      if (!c.never_used && !c.never_promoted &&
          c.rejection_only_total >= 3 && c.rejection_only_rate > 0.5) {
        flakyGate++;
      }
    }
    return { totalConfigs: data.configs.length, neverUsed, neverPromoted, flakyGate, wastedCost };
  }, [data]);

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>Config promotion leaderboard</h2>
      <div className="card" style={{
        background: "#0c1e3d", border: "1px solid #1e40af", borderLeft: "4px solid #3b82f6",
        fontSize: 13, lineHeight: 1.55, color: "#e2e8f0",
      }}>
        <p style={{ marginTop: 0 }}>
          Which saved configs have actually paid for themselves? This page joins{" "}
          <strong>TpeFeatureConfigsTable</strong> (every saved config), <strong>TpeRunHistoryTable</strong>{" "}
          (every SUCCEEDED run that used the config), and <strong>TradingModels</strong> (every
          ticker promotion ever — retired rows still count, because they represent a config that
          produced a tradeable model at some point).
        </p>
        <p style={{ marginBottom: 0 }}>
          <strong>Denominator</strong> = (run × ticker) pairs. <strong>Numerator</strong> = distinct
          (run_id, ticker) pairs in TradingModels. The <em>never promoted</em> chip lights up when a
          config has been run on{" "}
          <strong>{data?.rule_min_runs ?? 3}+ distinct days ≥ {data?.rule_min_gap_days ?? 3} days apart</strong>{" "}
          and the numerator is still 0. That's the "stop training" signal.
        </p>
      </div>

      {error && (
        <div className="card" style={{ color: "#fca5a5" }}>
          Failed to load: {String((error as Error).message || error)}
        </div>
      )}

      {/* Summary stat cards. Big numbers up top so the user sees the
          total dollar exposure to never-promoted configs without
          scanning the whole table. */}
      <div className="card" style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        <StatCard label="Saved configs" value={summary.totalConfigs} tone="neutral" />
        <StatCard label="Never run" value={summary.neverUsed} tone={summary.neverUsed > 0 ? "warn" : "neutral"} icon="🛑" />
        <StatCard label="Never promoted" value={summary.neverPromoted} tone={summary.neverPromoted > 0 ? "warn" : "neutral"} icon="🛑" />
        <StatCard label="Flaky CV gate" value={summary.flakyGate} tone={summary.flakyGate > 0 ? "warn" : "neutral"} icon="⚠️" />
        <StatCard label="$ on never-promoted" value={fmtUsd(summary.wastedCost)} tone={summary.wastedCost > 0 ? "warn" : "neutral"} icon="💸" />
      </div>

      <div className="card" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ color: "#94a3b8", fontSize: 12 }}>Sort:</span>
        {[
          { k: "default" as const, label: "Worst-first (default)" },
          { k: "cost" as const, label: "$ spent" },
          { k: "rate" as const, label: "Promotion rate" },
          { k: "runs" as const, label: "Runs" },
          { k: "name" as const, label: "Name" },
        ].map((opt) => (
          <button
            key={opt.k}
            onClick={() => setSortKey(opt.k)}
            style={{
              background: sortKey === opt.k ? "#1e3a8a" : "#0f172a",
              color: sortKey === opt.k ? "#bfdbfe" : "#cbd5e1",
              border: "1px solid #334155",
              borderRadius: 4, padding: "4px 10px", fontSize: 12, cursor: "pointer",
              fontWeight: sortKey === opt.k ? 600 : 400,
            }}
          >{opt.label}</button>
        ))}
        <label style={{ marginLeft: 12, color: "#94a3b8", fontSize: 12, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={hideHealthy}
            onChange={(e) => setHideHealthy(e.target.checked)}
            style={{ marginRight: 4 }}
          />
          Only show retirement candidates
        </label>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          style={{
            marginLeft: "auto",
            background: "#0f172a", color: "#cbd5e1",
            border: "1px solid #334155",
            borderRadius: 4, padding: "4px 10px", fontSize: 12,
            cursor: isFetching ? "wait" : "pointer",
          }}
        >{isFetching ? "Refreshing…" : "Refresh"}</button>
      </div>

      <div className="card">
        {isLoading ? (
          <div style={{ color: "#94a3b8", fontStyle: "italic" }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div style={{ color: "#94a3b8", fontStyle: "italic" }}>
            {hideHealthy ? "No retirement candidates 🎉" : "No saved configs."}
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Config</th>
                  <th className="num">Runs</th>
                  <th className="num" title="(run × ticker) pairs — the denominator for promotion rate.">Trials</th>
                  <th className="num" title="Distinct (run_id, ticker) pairs in TradingModels attributed to this config (retired models still count).">Promo'd</th>
                  <th className="num">Rate</th>
                  <th
                    className="num"
                    title="Distinct (run × ticker) attempts where every Phase 2 candidate failed the promote-time consistency gate. Subtitle = raw audit-row count (walk-down means one pair can produce several events)."
                  >Rejected</th>
                  <th className="num">$ spent</th>
                  <th>Last promotion</th>
                  <th>Run cadence</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  const status = classify(c);
                  return (
                    <tr key={c.config_name}>
                      <td>
                        <span
                          title={status.tooltip}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 4,
                            background: status.bg,
                            border: `1px solid ${status.border}`,
                            color: status.fg,
                            padding: "2px 8px", borderRadius: 4,
                            fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
                          }}
                        >
                          <span>{status.glyph}</span>
                          <span>{status.label}</span>
                        </span>
                      </td>
                      <td>
                        <Link
                          to="/admin/configs"
                          state={{ openConfigName: c.config_name }}
                          style={{ color: "#e0e7ff", fontWeight: 700, textDecoration: "none" }}
                        >
                          <code style={{ fontSize: 12 }}>{c.config_name}</code>
                        </Link>
                        {c.description && (
                          <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 2, lineHeight: 1.4 }}>
                            {c.description}
                          </div>
                        )}
                      </td>
                      <td className="num" title={`${c.well_spaced_run_count} well-spaced (≥3 days apart)`}>
                        {c.runs_total}
                        {c.runs_total > 0 && (
                          <div style={{ fontSize: 9, color: "#64748b" }}>
                            {c.well_spaced_run_count}/3 spaced
                          </div>
                        )}
                      </td>
                      <td className="num">{c.run_ticker_pairs_total || "—"}</td>
                      <td className="num" style={{ color: c.promotions_total > 0 ? "#56B4E9" : "#94a3b8", fontWeight: 600 }}>
                        {c.promotions_total}
                      </td>
                      <td className="num" style={{ color: c.promotion_rate > 0 ? "#cbd5e1" : "#475569" }}>
                        {fmtPct(c.promotion_rate, c.run_ticker_pairs_total)}
                      </td>
                      <td
                        className="num"
                        style={{
                          color: c.rejection_only_total > 0 ? "#f59e0b" : "#475569",
                          fontWeight: c.rejection_only_total > 0 ? 600 : 400,
                        }}
                        title={
                          c.rejection_event_count === 0
                            ? "No promote-time gate failures recorded."
                            : `${c.rejection_only_total} attempts ended with nothing promoted (${(c.rejection_only_rate * 100).toFixed(1)}% of ${c.run_ticker_pairs_total}). ` +
                              `${c.rejection_event_count} total audit rows (walk-down). ` +
                              `Last: ${fmtDate(c.last_rejection_at)}.`
                        }
                      >
                        {c.rejection_only_total > 0 ? c.rejection_only_total : "—"}
                        {c.rejection_event_count > c.rejection_only_total && (
                          <div style={{ fontSize: 9, color: "#64748b" }}>
                            {c.rejection_event_count} events
                          </div>
                        )}
                      </td>
                      <td className="num" style={{ color: "#cbd5e1" }}>
                        {fmtUsd(c.total_cost_usd)}
                      </td>
                      <td style={{ color: c.last_promotion_at ? "#cbd5e1" : "#475569", fontSize: 11 }}>
                        {fmtDate(c.last_promotion_at)}
                      </td>
                      <td>
                        <DateSparkline dates={c.run_dates} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data?.generated_at && (
        <div style={{ color: "#64748b", fontSize: 11, fontStyle: "italic", marginTop: 4 }}>
          Generated at {new Date(data.generated_at).toLocaleString()}. Aggregates across the full
          TpeRunHistoryTable + TradingModels scan, so the data is live (no warm cache).
        </div>
      )}
    </div>
  );
}

function StatCard({
  label, value, tone, icon,
}: {
  label: string;
  value: number | string;
  tone: "neutral" | "warn";
  icon?: string;
}) {
  const fg = tone === "warn" ? "#fed7aa" : "#e2e8f0";
  const border = tone === "warn" ? "#c2410c" : "#334155";
  const bg = tone === "warn" ? "#1c1917" : "#0f172a";
  return (
    <div style={{
      flex: "0 1 180px",
      background: bg,
      border: `1px solid ${border}`,
      borderRadius: 6,
      padding: "10px 14px",
    }}>
      <div style={{ color: "#94a3b8", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </div>
      <div style={{ color: fg, fontSize: 24, fontWeight: 700, marginTop: 2 }}>
        {icon && <span style={{ marginRight: 6 }}>{icon}</span>}
        {value}
      </div>
    </div>
  );
}
